import React, { FC, ReactNode, useEffect, useMemo, useState } from "react"
import {
    Button,
    Checkbox,
    FormattedMessage,
    Grid,
    GridColumn,
    Icon,
    PageTitle,
    Table,
    ToastLevel,
    useIntl,
    useToast,
} from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import sensorBatchStore from "../stores/sensor-batch-store"
import sensorStore from "../../sensors/stores/sensor-store"
import { NavigationDeleteEntityOptions, NavigationModalOptions } from "../../../../components/utils/modal-helper"
import { itemViewCellStyles, itemViewDetailsTableStyles, itemViewSubtitleStyles } from "../../../../components/common-style/common-style"
import { downloadFileFromBlobUrl } from "../../../../components/utils/files-utils"
import { getSensorBatchCsv, getSensorBatchPdf } from "../services/sensor-batch-service"
import SensorBatchModal from "../components/sensor-batch-modal"
import { Sensor, SensorTable } from "../../sensors/models/sensor-models"
import ListView, { TemplateItem } from "../../../../components/utils/list-view/list-view"
import TooltipBox from "../../../../components/common/tooltip-box"
import LowSpinner from "../../../../components/common/low-spinner"
import { convertDateToDDMMMYY, convertDateToDDMMMYYYY } from "../../../../components/utils/date-formatter-utils"
import IotEntityDeleteModal from "../../common/iot-entity-delete-modal"
import { IotDevicesModalTypes } from "../../common/iot-entity-modal-helper"
import { SBSensor } from "features/iot/sensor-batch/models/sensor-batch-models"
import SensorImportModal from "../../sensors/components/sensor-import-modal/sensor-import-modal"
import GenerateSensorsModal from "../../sensors/components/sensor-generate-modal/sensor-generate-sensors-modal"
import SensorsToggleModal from "features/iot/sensors/components/sensors-toggle-modal/sensors-toggle-modal"

const SensorBatchView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const navigate = useNavigate()
    const { sensorBatchId } = useParams()
    const location = useLocation()
    const { sensorBatchMap } = sensorBatchStore
    const sensorBatch = sensorBatchMap[sensorBatchId || ""]
    let failurCount: number = 0
    const params: NavigationModalOptions = location.state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
    const [isSensorDeleteModalOpen, setIsSensorDeleteModalOpen] = useState<boolean>(false)
    const [isSensorImportModalOpen, setIsSensorImportModalOpen] = useState<boolean>(false)
    const [sensorToEdit, setSensorToEdit] = useState<SBSensor | null>(null)
    const [sensorToDelete, setSensorToDelete] = useState<SBSensor | null>(null)
    const [downloadLoading, setDownloadLoading] = useState<boolean>(false)
    const [loadingData, setLoadingData] = useState<boolean>(true)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isGenerateSensorsModalOpen, setIsGenerateSensorsModalOpen] = useState(false)
    const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false)
    const [isSensorsToggleOpen, setIsSensorsToggleOpen] = useState<boolean>(false)
    const [hoveredSensor, setHoveredSensor] = useState<string | null>(null)
    const sensorBatchDate = convertDateToDDMMMYYYY(sensorBatch?.batchDate)
    const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>({})
    const [loadedSensorsData, setLoadedSensorsData] = useState<Sensor[]>([])
    const showNotifier = (text: string) => {
        setMessage(text, ToastLevel.SUCCESS, 6000)
    }
    const loadSensorData = async () => {
        try {
            setLoadingData(true)
            await sensorStore.loadSensors(sensorBatchId as string) // Load sensors into store
            const loadedSensors = sensorStore.sensors
            failurCount = loadedSensors?.filter(s => s.fault).length
            setLoadedSensorsData(loadedSensors) // Set the loaded sensors data
        } catch (error) {
            console.error("Error loading sensors:", error)
        } finally {
            new Promise(resolve => setTimeout(resolve, 1500))
            setLoadingData(false)
        }
    }

    const sensorTable: SensorTable[] = useMemo(() => {
        if (!sensorBatch?.sensors) return []

        return sensorBatch.sensors.map(sbs => {
            const sensor = sensorStore.getSensorByMacAddress(sbs.macAddress)

            return {
                id: sbs.macAddress,
                isChecked: checkedMap[sbs.macAddress] ?? false,
                serialNumber: sbs.serialNumber,
                macAddress: sbs.macAddress,
                isManufactured: sbs.isManufactured,
                manufacturedDate: sbs.manufacturedDate,
                firmwareRevision: sbs.firmwareRevision,
                fault: sensor?.fault ?? "",
                lqi: sensor?.lqi ?? "",
                wearDepth: sensor?.currentReportValue ?? "",
                date: sbs.manufacturedDate,
            }
        })
    }, [sensorBatch?.sensors, sensorStore.sensors, checkedMap])

    // Trigger sensor data loading on component mount
    useEffect(() => {
        loadSensorData()
        sensorBatchStore.checkFilesBySensorBatchId(sensorBatchId as string)
    }, [sensorBatchId])

    useEffect(() => {
        if (params?.type === "created") {
            showNotifier("New Sensors created successfully")
        }
        if (params?.type === "edited") {
            showNotifier("All changes made successfully")
        }
        window.history.replaceState({}, document.title)
    }, [params])

    const breadcrumbsPath: { text: string; to: string }[] = [
        {
            text: formatMessage("sensorBatches.title"),
            to: `/sensor-batches`,
        },
        {
            text: sensorBatchDate,
            to: `/sensor-batches/${sensorBatch?.id}`,
        },
    ]

    const downloadSensorBatchPdf = () => {
        setDownloadLoading(true)
        getSensorBatchPdf(sensorBatchId as string)
            .then(blobUrl => {
                if (blobUrl) {
                    downloadFileFromBlobUrl(blobUrl, `SensorBatch_${sensorBatchId}-Pdf.pdf`)
                } else {
                    setMessage("The requested file could not be found", ToastLevel.WARNING, 6000)
                }
            })
            .catch(err => {
                console.error("An error occurred:", err)
                setMessage("An error occurred while downloading the file", ToastLevel.ALERT, 6000)
            })
            .finally(() => {
                setDownloadLoading(false)
            })
    }

    const downloadSensorBatchCsv = () => {
        setDownloadLoading(true)
        getSensorBatchCsv(sensorBatchId as string)
            .then(blobUrl => {
                if (blobUrl) {
                    downloadFileFromBlobUrl(blobUrl, `SensorBatch_${sensorBatchId}-Csv.csv`)
                } else {
                    setMessage("The requested file could not be found", ToastLevel.WARNING, 6000)
                }
            })
            .catch(err => {
                console.error("An error occurred:", err)
                setMessage("An error occurred while downloading the file", ToastLevel.ALERT, 6000)
            })
            .finally(() => {
                setDownloadLoading(false)
            })
    }
    const editSensorBatchModal = {
        open: () => {
            setIsEditModalOpen(true)
        },
        close: (passed: boolean, sensorBatchId?: string, navigationCustomerOptions?: NavigationModalOptions) => {
            setIsEditModalOpen(false)
            if (passed) {
                navigate(location.pathname, {
                    state: navigationCustomerOptions,
                })
            }
        },
    }
    const deleteSensorBatchModal = {
        open: () => {
            setIsDeleteModalOpen(true)
        },
        close: (params: NavigationDeleteEntityOptions) => {
            setIsDeleteModalOpen(false)
            if (params.deleted) {
                navigate(`/sensor-batches`, { state: params })
            }
        },
    }
    const sensorsActionsHandlers = {
        open: () => {
            setIsSensorImportModalOpen(true)
        },
        edit: (event: React.MouseEvent, sensor: SensorTable) => {
            event.stopPropagation()
            const sbSensor = sensorBatchStore.getSensorByMacAddress(sensor.macAddress) ?? null
            setSensorToEdit(sbSensor)
            setIsSensorImportModalOpen(true)
        },
        delete: (event: React.MouseEvent, sensor: SensorTable) => {
            event.stopPropagation()
            const sbSensorToDelete = sensorBatchStore.getSensorByMacAddress(sensor.macAddress)
            if (sbSensorToDelete === undefined) {
                setMessage("Sensor not found.", ToastLevel.ALERT, 6000)
                return
            }
            setSensorToDelete(sbSensorToDelete)
            setIsSensorDeleteModalOpen(true)
        },
        close: () => {
            setIsSensorImportModalOpen(false)
            setSensorToEdit(null)
            setIsGenerateSensorsModalOpen(false)
        },
        closeDelete: (params: NavigationDeleteEntityOptions) => {
            setIsSensorDeleteModalOpen(false)
        },
        toggle: async () => {
            const selectedRows = sensorTable.filter(s => s.isChecked)
            if (selectedRows.length === 0) return

            setIsSensorsToggleOpen(true)
        },
        generateSensors: () => {
            setIsGenerateSensorsModalOpen(true)
        },
    }
    const handleCheckboxChange = (sensorId: string) => {
        setCheckedMap(prev => ({
            ...prev,
            [sensorId]: !prev[sensorId],
        }))
    }
    const toggleSelectAllSensors = (isChecked: boolean) => {
        const newCheckedMap: Record<string, boolean> = {}
        sensorBatch?.sensors?.forEach(s => {
            newCheckedMap[s.macAddress] = isChecked
        })
        setCheckedMap(newCheckedMap)
    }
    const template: TemplateItem<SensorTable>[] = [
        {
            headingKey: "",
            headingCellfun: () => (
                <Checkbox
                    checked={sensorTable.filter(s => s.isChecked).length === sensorTable.length}
                    onChange={(event, data) => toggleSelectAllSensors(data.checked as boolean)}
                />
            ),
            cellFunc: (s: SensorTable) => <Checkbox checked={s.isChecked} onChange={() => handleCheckboxChange(s.id)} />,
            isSearchable: false,
        },
        {
            headingKey: "headings.serialNumber",
            cellFunc: (s: SensorTable) => {
                return s.serialNumber
            },
            isSearchable: true,
        },
        {
            headingKey: "headings.macAddress",
            cellFunc: (s: SensorTable) => s.macAddress,
            isSearchable: true,
        },
        {
            headingKey: "headings.fault",
            cellFunc: (s: SensorTable) =>
                s.fault ? (
                    <span onMouseEnter={() => setHoveredSensor(s.serialNumber)} onMouseLeave={() => setHoveredSensor(null)}>
                        {hoveredSensor === s.serialNumber && <TooltipBox message={s.fault} position={{ right: "0", left: "2em" }} />}
                        <Icon name='icon-alarm-16' style={{ color: "red" }} />
                    </span>
                ) : (
                    ""
                ),
        },
        {
            headingKey: "headings.isManufactured",
            cellFunc: (s: SensorTable) => <Checkbox checked={s.isManufactured} readOnly />,
            isSearchable: false,
        },
        {
            headingKey: "headings.manufacturedDate",
            cellFunc: (s: SensorTable) => convertDateToDDMMMYY(s.manufacturedDate ?? null),
            isSearchable: true,
        },
        {
            headingKey: "headings.firmwareVersion",
            cellFunc: (s: SensorTable) => s?.firmwareRevision,
            isSearchable: true,
        },
        {
            headingKey: "headings.wearDepth",
            cellFunc: (s: SensorTable) => s?.wearDepth,
            isSearchable: true,
        },
        {
            headingKey: "headings.lQI",
            cellFunc: (s: SensorTable) => s?.lqi,
            isSearchable: true,
        },
        {
            headingKey: "headings.date",
            cellFunc: (s: SensorTable) => (s?.date ? convertDateToDDMMMYY(s.date) : ""),
            isSearchable: true,
        },
        {
            headingKey: "headings.actions",
            cellFunc: (s: SensorTable) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    <Icon name='icon-edit-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => sensorsActionsHandlers.edit(event, s)} />
                    <Icon name='icon-trash-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => sensorsActionsHandlers.delete(event, s)} />
                </span>
            ),
        },
    ]
    const rightPageButtons: ReactNode = (
        <>
            <Button icon='icon-trash-16' labelPosition='right' onClick={deleteSensorBatchModal.open} secondary>
                {formatMessage("sensorBatch.delete")}
            </Button>
            <Button icon='icon-edit-16' labelPosition='right' onClick={editSensorBatchModal.open} primary>
                {formatMessage("sensorBatch.edit")}
            </Button>
        </>
    )

    const rightSensorButtons: ReactNode = (
        <div style={{ display: "flex", gap: "0.5em" }}>
            <Button
                icon='icon-submitted-16'
                labelPosition='right'
                onClick={sensorsActionsHandlers.toggle}
                disabled={sensorTable.filter(s => s.isChecked).length === 0}
                loading={isLoadingButton}
            >
                {formatMessage("sensor.toggle")}
            </Button>
            <Button icon='icon-download-16' labelPosition='right' onClick={sensorsActionsHandlers.generateSensors} loading={isLoadingButton} primary>
                {formatMessage("sensor.generate")}
            </Button>
            <Button icon='icon-queued-16' labelPosition='right' onClick={sensorsActionsHandlers.open} loading={isLoadingButton} primary>
                {formatMessage("sensor.import")}
            </Button>
        </div>
    )
    const tableRows = [
        { header: "headings.batchDate", value: sensorBatchDate },
        { header: "headings.description", value: sensorBatch?.description },
        { header: "headings.manufacturer", value: sensorBatch?.manufacturer },
        { header: "headings.purchaseOrder", value: sensorBatch?.purchaseOrder },
        { header: "headings.partNumber", value: sensorBatch?.partNumber },
        {
            header: "headings.testResult",
            value: !sensorBatch?.isExistPdf ? "Not Exist" : "testresult.pdf",
        },
        {
            header: "headings.mIBConfigs",
            value: !sensorBatch?.isExistCsv ? "Not Exist" : "MIBConfigs.csv",
        },
    ]
    const tableContainerStyles = {
        borderTop: "2px solid gray",
        height: "91%",
    }
    return (
        <>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={sensorBatchDate} extraItems={rightPageButtons} />
            <Grid style={{ height: "90%", width: "100%", paddingTop: "1em" }}>
                <GridColumn
                    style={{
                        width: "25%",
                        borderRight: "2px solid gray",
                        paddingLeft: "2em",
                    }}
                >
                    <h3 style={itemViewSubtitleStyles}>
                        <FormattedMessage id='sensorBatch.details' />
                    </h3>
                    <Table style={itemViewDetailsTableStyles} tight>
                        <Table.Body>
                            {tableRows.map((row, index) => (
                                <Table.Row key={index}>
                                    <Table.HeaderCell style={itemViewCellStyles}>
                                        <FormattedMessage id={row.header} />
                                    </Table.HeaderCell>
                                    <Table.Cell style={itemViewCellStyles}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "10px",
                                            }}
                                        >
                                            <span>{row.value}</span>
                                            {row.header === "headings.testResult" && sensorBatch?.isExistPdf && (
                                                <Button
                                                    icon='icon-download-16'
                                                    onClick={downloadSensorBatchPdf}
                                                    loading={downloadLoading}
                                                    disabled={downloadLoading}
                                                    primary
                                                />
                                            )}
                                            {row.header === "headings.mIBConfigs" && sensorBatch?.isExistCsv && (
                                                <Button
                                                    icon='icon-download-16'
                                                    onClick={downloadSensorBatchCsv}
                                                    loading={downloadLoading}
                                                    disabled={downloadLoading}
                                                    primary
                                                    style={{ marginTop: "2px" }}
                                                />
                                            )}
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </GridColumn>
                <GridColumn style={{ width: "75%", height: "100%", overflow: "visible" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                        }}
                    >
                        <h3 style={itemViewSubtitleStyles}>
                            <FormattedMessage id='sensor.title' />
                        </h3>
                        {rightSensorButtons}
                    </div>
                    {`${failurCount} of ${sensorTable?.length} have failures.`}
                    <div style={tableContainerStyles}>
                        {loadingData ? (
                            <LowSpinner id={"sensor.loading"} />
                        ) : sensorTable?.length < 1 ? (
                            <span>No Data has been found</span>
                        ) : (
                            <ListView
                                noneYetKey={"sensor.nonYet"}
                                template={template}
                                data={sensorTable}
                                /*onClickRow={(s: Sensor) => navigate("/sensor-batches/" + s.id)}*/
                            />
                        )}
                    </div>
                </GridColumn>
            </Grid>
            <SensorBatchModal isOpen={isEditModalOpen} sensorBatch={sensorBatch} onClose={editSensorBatchModal.close} />
            <IotEntityDeleteModal
                modalType={IotDevicesModalTypes.SensorBatchModal}
                entity={sensorBatch}
                onClose={deleteSensorBatchModal.close}
                isOpen={isDeleteModalOpen}
            />
            <IotEntityDeleteModal
                modalType={IotDevicesModalTypes.SensorModal}
                entity={sensorToDelete as SBSensor}
                onClose={sensorsActionsHandlers.closeDelete}
                isOpen={isSensorDeleteModalOpen}
            />
            <SensorImportModal
                isOpen={isSensorImportModalOpen}
                onClose={sensorsActionsHandlers.close}
                sensorBatchId={sensorBatchId as string}
                sensor={sensorToEdit ?? undefined}
            />
            <GenerateSensorsModal
                isOpen={isGenerateSensorsModalOpen}
                onClose={sensorsActionsHandlers.close}
                sensorBatchId={sensorBatchId as string}
            />
            <SensorsToggleModal
                isOpen={isSensorsToggleOpen}
                setIsOpen={setIsSensorsToggleOpen}
                sensorTable={sensorTable}
            />
        </>
    )
})
export default SensorBatchView
